import { useEffect, useRef, useState } from 'react';
import { Alert, DatePicker, Form, Input, InputNumber, Modal, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useCreateInvoice } from '@hooks/useFinance';
import { useCustomerById, useCustomerSearch } from '@hooks/useSales';
import { useAuth } from '@stores/AuthContext';
import { mdCanReadModule } from '@stores/modulePermissions';
import type { SalesOrderDetail } from '@/types/sales';

// Термін оплати, якщо картка клієнта недоступна (немає доступу до модуля Customers)
const MD_DEFAULT_PAYMENT_TERM_DAYS = 30;

const MD_CURRENCY_OPTIONS = [
  { value: 'UAH', label: 'UAH — Гривня' },
  { value: 'USD', label: 'USD — Долар' },
  { value: 'EUR', label: 'EUR — Євро' },
];

// CreateInvoiceCommandValidator: TotalAmount > 0, ExchangeRate > 0
const mdPositiveRule = (mdMessage: string) => ({
  validator: (_: unknown, mdValue?: number | null) =>
    mdValue == null || mdValue > 0 ? Promise.resolve() : Promise.reject(new Error(mdMessage)),
});

interface CreateInvoiceFormValues {
  customerId?: string;
  issueDate: Dayjs;
  dueDate: Dayjs;
  totalAmount: number;
  currency: string;
  exchangeRate: number;
  notes?: string;
}

interface CreateInvoiceModalProps {
  open: boolean;
  /** Замовлення, за яким виставляється рахунок; без нього — ручний рахунок з вибором клієнта */
  salesOrder?: SalesOrderDetail;
  onClose: () => void;
  onCreated: (mdInvoiceId: string) => void;
}

export default function CreateInvoiceModal({ open, salesOrder, onClose, onCreated }: CreateInvoiceModalProps) {
  const [mdForm] = Form.useForm<CreateInvoiceFormValues>();
  const [mdCustomerSearchText, setMdCustomerSearchText] = useState('');
  // isFieldTouched не годиться: setFieldsValue теж позначає поле «торкнутим»
  const mdIsDueDateEditedRef = useRef(false);
  const { user } = useAuth();
  const mdCanReadCustomers = mdCanReadModule(user?.roles ?? [], 'Customers');

  const mdWatchedCustomerId = Form.useWatch('customerId', mdForm);
  const mdCurrency = Form.useWatch('currency', mdForm);
  const mdCustomerId = salesOrder?.customerId ?? mdWatchedCustomerId;

  // Із замовлення клієнт уже відомий — пошук не потрібен
  const { data: mdCustomerOptions, isFetching: mdCustomersLoading } = useCustomerSearch(
    salesOrder ? '' : mdCustomerSearchText
  );
  // Картку клієнта без доступу до Customers не запитуємо (403) — лишається типовий термін
  const { data: mdCustomerDetail } = useCustomerById(
    open && mdCanReadCustomers ? mdCustomerId : undefined
  );
  const mdPaymentTermDays =
    mdCustomerDetail && mdCustomerDetail.id === mdCustomerId ? mdCustomerDetail.paymentTermDays : undefined;
  const mdCreateInvoice = useCreateInvoice();

  useEffect(() => {
    if (!open) return;
    mdForm.resetFields();
    mdIsDueDateEditedRef.current = false;
    setMdCustomerSearchText('');
    const mdToday = dayjs().startOf('day');
    mdForm.setFieldsValue({
      issueDate: mdToday,
      dueDate: mdToday.add(MD_DEFAULT_PAYMENT_TERM_DAYS, 'day'),
      // Сума — як її рахує сервер у SalesOrderDetailDto; валюта й курс — із замовлення
      ...(salesOrder
        ? { totalAmount: salesOrder.totalAmount, currency: salesOrder.currency, exchangeRate: salesOrder.exchangeRate }
        : { currency: 'UAH', exchangeRate: 1 }),
    });
  }, [open, salesOrder, mdForm]);

  // Термін оплати клієнта підставляємо, доки користувач не змінив дату вручну
  useEffect(() => {
    if (!open || mdPaymentTermDays === undefined || mdIsDueDateEditedRef.current) return;
    const mdIssueDate = mdForm.getFieldValue('issueDate') as Dayjs | undefined;
    mdForm.setFieldsValue({
      dueDate: (mdIssueDate ?? dayjs().startOf('day')).add(mdPaymentTermDays, 'day'),
    });
  }, [open, mdPaymentTermDays, mdCustomerId, mdForm]);

  const mdHandleSubmit = async () => {
    try {
      const mdValues = await mdForm.validateFields();
      const mdCustomerIdToSend = salesOrder?.customerId ?? mdValues.customerId;
      if (!mdCustomerIdToSend) return;
      const mdInvoiceId = await mdCreateInvoice.mutateAsync({
        customerId: mdCustomerIdToSend,
        salesOrderId: salesOrder?.id,
        issueDate: mdValues.issueDate.format('YYYY-MM-DD'),
        dueDate: mdValues.dueDate.format('YYYY-MM-DD'),
        totalAmount: mdValues.totalAmount,
        currency: mdValues.currency,
        exchangeRate: mdValues.currency === 'UAH' ? 1 : mdValues.exchangeRate,
        notes: mdValues.notes?.trim() || undefined,
      });
      onCreated(mdInvoiceId);
    } catch {
      // Помилки полів показує форма, відмову сервера — useCreateInvoice
    }
  };

  return (
    <Modal
      title={salesOrder ? `Рахунок за замовленням ${salesOrder.number}` : 'Новий рахунок'}
      open={open}
      onOk={mdHandleSubmit}
      onCancel={onClose}
      confirmLoading={mdCreateInvoice.isPending}
      okText="Створити"
      cancelText="Скасувати"
      width={640}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Рахунок створюється чернеткою — виставити його можна на картці рахунку"
      />
      <Form form={mdForm} layout="vertical">
        {salesOrder ? (
          <Form.Item label="Клієнт">
            <Input value={salesOrder.customerName} disabled />
          </Form.Item>
        ) : (
          <Form.Item
            name="customerId"
            label="Клієнт"
            rules={[{ required: true, message: 'Оберіть клієнта' }]}
          >
            <Select
              showSearch
              placeholder="Почніть вводити код або назву (від 2 символів)"
              filterOption={false}
              onSearch={setMdCustomerSearchText}
              loading={mdCustomersLoading}
              notFoundContent={mdCustomerSearchText.length < 2 ? 'Введіть щонайменше 2 символи' : 'Нічого не знайдено'}
              options={mdCustomerOptions?.map((mdCust) => ({
                value: mdCust.id,
                label: `${mdCust.code} — ${mdCust.name}`,
              }))}
            />
          </Form.Item>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <Form.Item
            name="issueDate"
            label="Дата рахунку"
            rules={[{ required: true, message: 'Оберіть дату' }]}
          >
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="dueDate"
            label="Термін оплати"
            dependencies={['issueDate']}
            rules={[
              { required: true, message: 'Оберіть термін оплати' },
              ({ getFieldValue }) => ({
                validator(_: unknown, mdValue?: Dayjs) {
                  const mdIssueDate = getFieldValue('issueDate') as Dayjs | undefined;
                  return !mdValue || !mdIssueDate || !mdValue.isBefore(mdIssueDate, 'day')
                    ? Promise.resolve()
                    : Promise.reject(new Error('Термін оплати не може бути раніше дати рахунку'));
                },
              }),
            ]}
          >
            <DatePicker
              format="DD.MM.YYYY"
              style={{ width: '100%' }}
              onChange={() => {
                mdIsDueDateEditedRef.current = true;
              }}
            />
          </Form.Item>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0 16px' }}>
          <Form.Item
            name="totalAmount"
            label="Сума"
            rules={[
              { required: true, message: 'Введіть суму' },
              mdPositiveRule('Сума має бути більшою за 0'),
            ]}
          >
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="currency"
            label="Валюта"
            rules={[{ required: true, message: 'Оберіть валюту' }]}
          >
            <Select options={MD_CURRENCY_OPTIONS} disabled={!!salesOrder} />
          </Form.Item>
          <Form.Item
            name="exchangeRate"
            label="Курс"
            rules={[
              { required: true, message: 'Введіть курс' },
              mdPositiveRule('Курс має бути більшим за 0'),
            ]}
          >
            <InputNumber min={0} precision={4} style={{ width: '100%' }} disabled={mdCurrency === 'UAH'} />
          </Form.Item>
        </div>
        <Form.Item name="notes" label="Примітки">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
